import { IDropzoneFile } from '@common/types/common';
import { IUser, Roles } from '@common/types/user';
import { FormikProps } from 'formik';
import { LinkProps } from 'react-router-dom';

enum EventTypes {
    PUBLIC_ONSITE = 'open-event',
    PRIVATE_ONSITE = 'private-event',
    PUBLIC_VIRTUAL = 'open-webinar',
    PRIVATE_VIRTUAL = 'private-webinar',
}

/** Underscore-separated event type key as returned in queryset_ids (e.g. "open_event", "private_webinar") */
type EventTypeKey = `${string & EventTypes}` extends `${infer A}-${infer B}` ? `${A}_${B}` : never;

enum EventLocation {
    ONSITE = 'event',
    VIRTUAL = 'webinar',
}

interface IEventCreation {
    type: EventLocation | '';
    privacy: 'public' | 'private' | '';
    name: string;
}

type ResendEmailSubscriberRecepientStatus = 'valid' | 'invalid';

interface IEventPermissions {
    id: number;
    role: Roles;
    add_guests: boolean;
    send_invites: boolean;
    edit: boolean;
    publish: boolean;
}

enum RegistrationCardStatus {
    /**
     * CARD_R2
     */
    ABLE_TO_REGISTER = 'ABLE_TO_REGISTER',
    /**
     * CARD_W3
     */
    MAX_CAPACITY_REACHED = 'MAX_CAPACITY_REACHED',
    /**
     * CARD_I1
     */
    UNABLE_TO_REGISTER = 'UNABLE_TO_REGISTER',
    /**
     * CARD_U2
     */
    REGISTERED = 'REGISTERED',
    /**
     * CARD_L1
     */
    AUTHENTICATION_NEEDED = 'AUTHENTICATION_NEEDED',
}

interface Market {
    id: number;
    title: Markets;
    slug: MarketSlugs;
}

interface IEvent {
    id: number;
    uuid: string;
    published: boolean;
    type: EventTypes;
    name: string;
    subtitle?: string;
    date_start: string | null;
    date_end: string | null;
    time_zone: string;
    country_name: string;
    country_code: string;
    country: string;
    participants: number;
    owner?: IEventUser;
    created_by?: string;
    archived?: boolean;
    room_url?: string;
    city?: string;
    location?: string;
    custom_location?: string;
    description?: string;
    markets: Market[];
    questionnaire_id?: string | null;
    enable_questionnaire?: boolean;
    subscription_status: RegistrationStatus;
    dropbox_link: string;
    registration_open: boolean;
    enable_external_registration: boolean;
    possible_subscribers: Roles[] | null;
    max_capacity: number;
    registration_url: string;
    number_attendees: number;
    send_a_reminder: boolean;
    invitee_qr_code?: boolean;
    invitation_email_type: 'auto' | 'manual';
    enable_followup: boolean;
    notify_admins_after_feedback?: boolean;
    notify_admins_after_registration?: boolean;
    sync_to_mailchimp: boolean;
    mailchimp_synced_at: string | null;
    is_pending_mailchimp_sync: boolean;
    organizers?: {
        id: number;
        user: IEventOrganizer;
    }[];
    hybrid_event?: {
        onsite_event: {
            id: number;
            name: string;
            guest_access_hash?: string;
        };
        online_event?: {
            id: number;
            name: string;
            guest_access_hash?: string;
        };
    };
    banner_image: string;
    cohostPermissions: IEventPermissions;
    enable_bring_a_colleague: boolean;
    enable_bring_a_colleague_button: boolean;
    application_status: RegistrationApplicationStatus;
    publicly_available: boolean;
    registration_status: RegistrationCardStatus;
    subscriber_status_updated_at: string | null;
    language_spoken?: string;
    subscriber_attend_dinner?: boolean;
    user_meets_level_badge_requirements?: {
        /**
         * Check `meets_requirements_without_temp_badges` for more details
         */
        meets_requirements: boolean;
        /**
         * If returned true, the user meets the required badges without considering temporary badges, so it's a normal registration.
         *
         * If returned false and `meets_requirements` is true, the user only meets the requirements because of temporary badges.
         *
         * If both are false, the user doesn't meet the requirements at all.
         */
        meets_requirements_without_temp_badges: boolean;
    };
    level_badge_requirements?: EventLevelBadgeRequirement | LogicalOperator;
    enable_waiting_list?: boolean;
    is_user_in_waiting_list: boolean;
    user_has_waiting_list_invitation: boolean;
    allow_temporary_registration?: boolean;
    user_has_temporary_registration?: boolean;
    allowed_countries?: string[];
}
type KeyOfEvent = keyof IEvent;
type OnSiteEventFields = Pick<
    IEvent,
    'country' | 'country_name' | 'country_code' | 'location' | 'custom_location' | 'subscriber_attend_dinner' | 'city'
>;
type WebinarEventFields = Pick<IEvent, 'room_url'>;

type ManagementEvent = Pick<
    IEvent,
    | 'id'
    | 'type'
    | 'published'
    | 'name'
    | 'date_start'
    | 'date_end'
    | 'time_zone'
    | 'participants'
    | 'max_capacity'
    | 'markets'
    | 'possible_subscribers'
    | 'enable_questionnaire'
    | 'registration_url'
    | 'enable_followup'
    | 'send_a_reminder'
    | 'invitee_qr_code'
    | 'invitation_email_type'
    | 'organizers'
    | 'hybrid_event'
    | 'mailchimp_synced_at'
    | 'banner_image'
    | 'cohostPermissions'
    | 'enable_bring_a_colleague'
    | 'is_pending_mailchimp_sync'
    | 'publicly_available'
    | 'registration_open'
    | 'sync_to_mailchimp'
    | 'owner'
    | 'level_badge_requirements'
    | 'city'
    | 'subtitle'
    | 'custom_location'
    | 'allow_temporary_registration'
    | 'notify_admins_after_feedback'
    | 'enable_waiting_list'
>;
type ManagementAnyEvent = ManagementEvent & Omit<OnSiteEventFields, 'country' | 'location'> & WebinarEventFields;
type ManagementEventListItem = Omit<ManagementAnyEvent, 'level_badge_requirements'> & {
    level_badge_requirements: EventLevelBadgeRequirement[];
};
type ManagementDetailsFields = Pick<
    IEvent,
    'description' | 'subtitle' | 'owner' | 'dropbox_link' | 'enable_questionnaire' | 'questionnaire_id' | 'max_capacity'
>;
type ManagementOnSiteEvent = Omit<ManagementEvent, 'owner_id'> &
    Omit<OnSiteEventFields, 'country_code'> &
    ManagementDetailsFields;
type ManagementWebinarEvent = Omit<ManagementEvent, 'owner_id'> & WebinarEventFields & ManagementDetailsFields;

type PublicEventDetails = Pick<
    IEvent,
    | 'id'
    | 'name'
    | 'date_start'
    | 'date_end'
    | 'time_zone'
    | 'number_attendees'
    | 'max_capacity'
    | 'markets'
    | 'hybrid_event'
    | 'description'
    | 'enable_questionnaire'
    | 'questionnaire_id'
    | 'enable_external_registration'
    | 'registration_url'
    | 'registration_status'
    | 'subscriber_status_updated_at'
    | 'language_spoken'
    | 'level_badge_requirements'
    | 'user_meets_level_badge_requirements'
    | 'enable_waiting_list'
    | 'subtitle'
    | 'allow_temporary_registration'
    | 'user_has_temporary_registration'
    | 'is_user_in_waiting_list'
    | 'user_has_waiting_list_invitation'
    | 'enable_bring_a_colleague_button'
> & { owner: IEventUser };
type PublicWebinarEvent = PublicEventDetails & WebinarEventFields & Pick<IEvent, 'published'>;
type PublicOnSiteEvent = PublicEventDetails &
    Omit<OnSiteEventFields, 'country_name' | 'country_code' | 'country'> &
    Pick<IEvent, 'published'> & { country?: { code: string; name: string } };

type RegistrationEvent = Pick<
    IEvent,
    | 'id'
    | 'type'
    | 'published'
    | 'name'
    | 'date_start'
    | 'date_end'
    | 'time_zone'
    | 'description'
    | 'possible_subscribers'
    | 'max_capacity'
    | 'number_attendees'
    | 'registration_open'
    | 'application_status'
    | 'subscription_status'
    | 'owner'
    | 'language_spoken'
    | 'user_meets_level_badge_requirements'
    | 'level_badge_requirements'
    | 'allow_temporary_registration'
> & { markets: number[] };
type RegistrationAnyEvent = RegistrationEvent & WebinarEventFields & Omit<OnSiteEventFields, 'country'>;
type RegistrationEventListItem = Omit<RegistrationAnyEvent, 'level_badge_requirements'> &
    Pick<IEvent, 'banner_image'> & {
        level_badge_requirements: EventLevelBadgeRequirement[];
    };

enum RegistrationStatus {
    ACCEPTED = 'accepted',
    DECLINED = 'declined',
    CANCELED = 'canceled',
    ABLE_TO_REGISTER = 'able_to_register',
    PENDING = 'pending',
    UNABLE_TO_REGISTER = 'unable_to_register',
}

enum RegistrationApplicationStatus {
    NONE = '',
    PENDING = 'pending_approval',
    ACCEPTED = 'application_accepted',
    DECLINED = 'application_rejected',
}

interface IEmail {
    date_created: string;
    date_sent: string;
}

interface IEventUser {
    id: number;
    full_name: string;
    email: string;
    role: Roles;
    company_name: string;
    country?: {
        code: string;
        name: string;
    };
    date_joined: string;
    food_preference?: IUser['food_preference'];
}

interface IEventOrganizer extends Omit<IEventUser, 'country' | 'date_joined'> {
    country_id: string;
    address1: string;
    city: string;
    state: string;
    mobile_phone: string;
    exact_debtor_number: string;
}

enum PresentStatus {
    YES = 'yes',
    NO = 'no',
    NA = 'na',
}

interface IAttendee {
    id: number;
    user: IEventUser;
    status: RegistrationStatus;
    added_by: string | null;
    present: PresentStatus;
    accepted_at: string | null;
    user_id: number;
    event_followup_evaluation: number | null;
    invitation_email: IEmail | null;
    event_followup_evaluation_email: IEmail | null;
    qr_code_email: IEmail | null;
    reminder_email: IEmail | null;
    group?: IBreakOutGroup;
    attend_dinner?: boolean;
    application_status: '' | 'pending_approval' | 'application_accepted' | 'application_rejected';
    is_temporary_registration: boolean;
}

interface ISubscriberFollowUp {
    id: number;
    user: Omit<IUser, 'first_name' | 'last_name'>;
    event_followup_evaluation: number | null;
    event_followup_evaluation_email: IEmail;
    event_followup_evaluation_date: string;
    event_followup_hash: string | null;
    event_followup_comment: string | null;
}

interface ISubscriberFollowUpRating {
    id: number;
    event_followup_evaluation: number;
    count: number;
    percentage: number;
}

interface ISubscribersStatus {
    status: 'accepted' | 'declined' | 'pending' | 'canceled' | 'able_to_register';
    count: number;
}

interface ISubscriberActivity {
    id: number;
    status: 'accepted' | 'declined' | 'pending' | 'canceled' | 'able_to_register' | 'on_waiting_list';
    status_updated_at: string | null;
    user: IUser;
}

interface ISubscriberFollowUp {
    id: number;
    user: Omit<IUser, 'first_name' | 'last_name'>;
    event_followup_evaluation: number | null;
    event_followup_evaluation_email: IEmail;
    event_followup_evaluation_date: string;
    event_followup_hash: string | null;
    event_followup_comment: string | null;
}

interface ISubscriberFollowUpRating {
    id: number;
    event_followup_evaluation: number;
    count: number;
    percentage: number;
}

interface ISubscribersStatus {
    status: 'accepted' | 'declined' | 'pending' | 'canceled' | 'able_to_register';
    count: number;
}

interface INewSubscriber {
    user: {
        first_name?: string;
        last_name?: string;
        email: string;
        company_name?: string;
        language?: string;
        country_code?: string;
    };
    group_id?: IBreakOutGroup['id'];
}

type BEAttendee = Omit<IAttendee, 'user'> & {
    user__full_name: string;
    user__company_name: string;
    user__role: string;
    user__country__code: string;
};

interface IEventDetailAccordionProps {
    isExpanded: boolean;
    handleAccordionChange: (event: React.SyntheticEvent<Element, Event>, expanded: boolean) => void;
}

interface IGroup {
    id: string;
    name: string;
    active_user_count: number;
    group_type: string;
}

enum Markets {
    PROFESSIONAL_VEHICLES = 'Professional Vehicles',
    RECREATIONAL_VEHICLES = 'Recreational Vehicles',
    PROFESSIONAL_MARINE = 'Professional Marine',
    LEISURE_MARINE = 'Leisure Marine',
    OFFGRID_BACKUP = 'Off-grid and Backup',
    ENERGY_STORAGE = 'Energy storage',
    INDUSTRIAL = 'Industrial',
    TELECOM = 'Telecom',
    MOBILITY = 'Mobility',
}

enum MarketSlugs {
    PROFESSIONAL_VEHICLES = 'professional-vehicles',
    INDUSTRIAL = 'industrial',
    PROFESSIONAL_MARINE = 'professional-marine',
    MOBILITY = 'mobility',
    OFFGRID_BACKUP = 'offgrid',
    ENERGY_STORAGE = 'consumption',
    TELECOM = 'telecom',
    RECREATIONAL_VEHICLES = 'recreational-vehicles',
    LEISURE_MARINE = 'leisure-marine',
}

type CohostDialogState = 'list' | 'success' | 'success-with-changes' | 'guest-feedback';

enum RegistrationActions {
    LOGIN = 'login',
    REGISTER = 'register',
    UNREGISTER = 'unregister',
    OPEN_TYPEFORM = 'open_typeform',
    EXTERNAL = 'external',
    INELIGIBLE = 'ineligible',
    LOADING = 'loading',
    PENDING = 'pending',
    /**
     * Invited by someone, isn't logged in
     */
    COLLEAGUE = 'colleague',
    /**
     * Invited by someone, logged in
     */
    COLLEAGUE_LOGIN = 'colleague-login',
}

type SalesManager = { id: number; full_name: string; first_name?: string; last_name?: string };

type EmailType = 'follow-up' | 'invitation' | 'reminder' | 'qr-code';

type Statistic = {
    status: 'processing' | 'done';
    sent: number;
    total: number;
    in_queue: number;
};
interface IBreakOutGroup {
    id: number;
    name: string;
    group_size: number;
    created_at?: string;
    deletePending?: boolean;
}

type IEventStatistics = {
    invitation: Statistic;
    reminder: Statistic;
    qr_code?: Statistic;
    follow_up: Statistic;
    uninvited_subscribers: number;
    subscribers_accepted_count: number;
    badge_eligible_users_count: number;
    events_rewarding_one_of_required_badges_count: number;
};

interface AttendeeTableRowOption<T = void> {
    label: string;
    onClick?: (arg: T) => void;
    disabled?: (arg: T) => boolean;
    isLoading?: boolean;
    items?: AttendeeTableRowOption<T>[];
    className?: string;
}

interface BlackListTableRowOption<T = void> {
    label: string;
    onClick?: (arg: T) => void;
    disabled?: (arg: T) => boolean;
    isLoading?: boolean;
    items?: BlackListTableRowOption<T>[];
    className?: string;
}

type EventFormikType = Partial<
    Omit<IEvent, 'organizers'> & {
        date_time_start: string | null;
        date_time_end: string | null;
        attendees: number;
        organizers_user_ids: number[];
        banner: IDropzoneFile[];
    }
>;

type BannerPreviewState = { bannerPreview: string | null; setBannerPreview: (value: string | null) => void };

type EventBannerProps = {
    isEdit: boolean;
    formik: FormikProps<EventFormikType>;
    previewState: BannerPreviewState;
};

interface IEventListCardProps extends Omit<LinkProps, 'to'>, Omit<React.RefAttributes<HTMLAnchorElement>, 'className'> {
    event?: RegistrationEventListItem;
    markets?: {
        id: number;
        title: Markets;
    }[];
    user?: Omit<IUser, 'full_name'>;
}

enum RegistrationLabel {
    UNPUBLISHED = 'Event not published',
    REGISTERED = 'You are registered',
    VISIBILITY_OFF = 'invisible',
    INELIGIBLE = 'Ineligible for registration',
    CLOSED = 'Registration closed',
    BADGES_REQUIRED = 'See Requirements',
    TEMPORARY = 'Temporary Registration',
}

type EventLevelBadgeRequirement = {
    id: number;
    level_badge: LevelBadge;
    timeframe?: string;
};

type EventLevelBadgeRewards = Omit<EventLevelBadgeRequirement, 'timeframe'>;

type LevelBadge = {
    id: number;
    badge: LevelBadge_Badge;
    level: Level;
    image: string;
};

// Badge-related types
enum BadgeLevel {
    BEGINNER,
    ADVANCED,
    EXPERT,
}

interface BadgeDisplayProps {
    badges: EventLevelBadgeRequirement[];
    visibleBadgeCount?: number;
}

type ISODuration = {
    years: number;
    months: number;
    weeks: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    totalMilliseconds?: number; // optional convenience field
};

type LevelBadge_Badge = {
    id: number;
    name: string;
};

type Level = {
    id: number;
    name: string;
    value: number;
};

type LogicalOperator = {
    logical_operator: 'or' | 'and';
    op1: EventLevelBadgeRequirement | LogicalOperator;
    op2: EventLevelBadgeRequirement | LogicalOperator;
};

type BadgesAndLevels = { badges: LevelBadge_Badge[]; levels: Level[] };

interface EventProps extends Pick<IEvent, 'id'> {
    type: EventTypes;
}

type WaitingListProps = EventProps & {
    email?: string;
    first_name?: string;
    last_name?: string;
    company_name?: string;
    country_code?: string;
    colleagueHash?: string | null;
    guestAccessHash?: string | null;
};

interface WaitingListResponse {
    guest_access_hash?: string;
}

type WaitingListEntry = {
    id: number;
    created_at: string;
    status: 'in_queue' | 'able_to_register' | 'accepted' | 'declined' | 'expired';
    hours_till_expiration: number | null;
    user: IEventUser;
    waiting_list_invitation_email: boolean;
    position: number;
};

type Colleague = {
    user: {
        first_name?: string;
        last_name?: string;
        full_name?: string;
        email: string;
        company_name?: string;
        language?: string;
    };
    user_id?: number;
};

type BlacklistEntry = {
    id: number;
    email: string;
    user: IEventUser;
    created_at: string;
};

type BlacklistTableHeader = BlacklistEntry & {
    subscriber__user__full_name: string;
    role: string;
    company: string;
};

type BlacklistDialogState = 'list' | 'add' | 'confirm' | 'success';

enum BLACKLIST_ADD_TABS {
    LIST = 0,
    USERS = 1,
    OTHER_EVENTS = 2,
}

type BreakoutGroupsLocalChanges = {
    created: Partial<IBreakOutGroup>[];
    modified: Partial<IBreakOutGroup>[];
    deleted: number[];
};

type SignInGuestDialogState = false | 'list' | 'print' | 'table-print' | 'print-success' | 'print-error';

type EventPrinter = {
    id: number;
    printer_friendly_name: string;
    printer_internal_name: string;
};

enum AttendeesTabs {
    ALL,
    AWAITING,
    SIGNED_IN,
}

type SignInGuestsFlowState = 'sign-in-list' | 'replace-select' | 'replace-create' | 'add-select' | 'add-create';
type EventTypeIdSetMap = Partial<Record<EventTypeKey, Set<number>>>;

type EventAnalyticsTraffic = {
    period?: string;
    distinct_views?: number;
    countries?: Record<
        string,
        {
            views: number;
            registrations: number;
            attendees: number;
        }
    >;
    traffic_sources?: Record<string, number>;
    device_types?: Record<string, number>;
    browser_types?: Record<string, number>;
    os_types?: Record<string, number>;
    hourly_views?: Record<string, number>;
};

type EventAnalyticsPerformance = {
    views_total?: number;
    registrations_total?: number;
    registrations_to_views_ratio?: number;
    attendees_total?: number;
    analytics_by_period?: { period?: string; views: number; registrations: number }[];
};

enum EventDetailTabs {
    ANALYTICS = 0,
    COHOST = 1,
    DETAILS = 2,
    REGISTRATION = 3,
    BADGES = 4,
    AUDIENCE = 5,
    OVERVIEW = 6,
}

export {
    AttendeesTabs,
    BadgeLevel,
    BLACKLIST_ADD_TABS,
    EventDetailTabs,
    EventLocation,
    EventTypes,
    Markets,
    MarketSlugs,
    PresentStatus,
    RegistrationActions,
    RegistrationApplicationStatus,
    RegistrationCardStatus,
    RegistrationLabel,
    RegistrationStatus,
};

export type {
    AttendeeTableRowOption,
    BadgeDisplayProps,
    BadgesAndLevels,
    BEAttendee,
    BlacklistDialogState,
    BlacklistEntry,
    BlacklistTableHeader,
    BlackListTableRowOption,
    BreakoutGroupsLocalChanges,
    CohostDialogState,
    Colleague,
    EmailType,
    EventAnalyticsPerformance,
    EventAnalyticsTraffic,
    EventBannerProps,
    EventFormikType,
    EventLevelBadgeRequirement,
    EventLevelBadgeRewards,
    EventPrinter,
    EventProps,
    EventTypeIdSetMap,
    EventTypeKey,
    IAttendee,
    IBreakOutGroup,
    IEmail,
    IEvent,
    IEventCreation,
    IEventDetailAccordionProps,
    IEventListCardProps,
    IEventOrganizer,
    IEventPermissions,
    IEventStatistics,
    IEventUser,
    IGroup,
    INewSubscriber,
    ISODuration,
    ISubscriberActivity,
    ISubscriberFollowUp,
    ISubscriberFollowUpRating,
    ISubscribersStatus,
    KeyOfEvent,
    Level,
    LevelBadge,
    LogicalOperator,
    ManagementAnyEvent,
    ManagementEventListItem,
    ManagementOnSiteEvent,
    ManagementWebinarEvent,
    Market,
    PublicOnSiteEvent,
    PublicWebinarEvent,
    RegistrationAnyEvent,
    RegistrationEventListItem,
    ResendEmailSubscriberRecepientStatus,
    SalesManager,
    SignInGuestDialogState,
    SignInGuestsFlowState,
    Statistic,
    WaitingListEntry,
    WaitingListProps,
    WaitingListResponse,
};
